import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { VariableSizeList as List } from 'react-window';
import { BACKEND_URL } from '../config';
import { useRefresh } from '../RefreshContext';
import { useLanguage } from '../contexts/LanguageContext';
import TableDisplay from './TableDisplay';

// Memoized OrderItemCard component to prevent unnecessary re-renders
const OrderItemCard = React.memo(({
    item,
    status,
    theme,
    t,
    currentTime,
    allOptions,
    onStatusUpdate,
    getTimeSinceOrder,
    getTimeInStatus,
    getTimeToFinish,
    getTimeColor,
    getOptionNames
}) => {
    const optionNames = getOptionNames(item, allOptions);

    return (
        <div
            onClick={() => onStatusUpdate(item.id, status)}
            style={{
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                marginBottom: '0.35rem',
                padding: '0.35rem',
                backgroundColor: item.dont_make === 1 ? 'rgba(255, 87, 34, 0.1)' : theme?.colors?.background,
                border: item.dont_make === 1 ? '1px solid #ff5722' : '1px solid #ddd',
                borderRadius: theme?.borderRadius || 6,
                cursor: 'pointer',
                transition: 'background-color 0.2s, transform 0.1s',
                overflow: 'hidden',
            }}
            onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = theme?.colors?.surface || '#f5f5f5';
                e.currentTarget.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = item.dont_make === 1 ? 'rgba(255, 87, 34, 0.1)' : theme?.colors?.background;
                e.currentTarget.style.transform = 'scale(1)';
            }}
        >
            {/* Prominent Table Display for Delivery Reference */}
            <div style={{
                backgroundColor: theme?.colors?.primary || '#1976d2',
                color: theme?.colors?.surface || '#fff',
                padding: '0.35rem',
                borderRadius: theme?.borderRadius || 6,
                marginBottom: '0.35rem',
                fontWeight: 'bold',
                fontSize: '0.95rem',
                textAlign: 'center'
            }}>
                {t('table')}: {item.table_name}
            </div>

            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.35rem',
                gap: '0.35rem'
            }}>
                <strong style={{
                    color: theme?.colors?.text,
                    wordBreak: 'break-word',
                    flex: '1 1 auto',
                    minWidth: 0,
                    fontSize: '0.9rem'
                }}>
                    {item.product_name} x{item.quantity}
                </strong>

                <div style={{
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    textAlign: 'right',
                    flexShrink: 0
                }}>
                    {item.status === 2 ? (
                        <div style={{ color: theme?.colors?.primary }}>
                            {t('timeToFinish')}: {getTimeToFinish(item)}
                        </div>
                    ) : (
                        <div style={{ color: getTimeColor(item) }}>
                            {t('order')}: {getTimeSinceOrder(item)}
                        </div>
                    )}
                    <div style={{ color: theme?.colors?.textSecondary }}>
                        {t('status')}: {getTimeInStatus(item)}
                    </div>
                </div>
            </div>

            {/* Display option names below product_name */}
            {optionNames && optionNames.length > 0 && (
                <div style={{
                    fontSize: '0.8rem',
                    color: theme?.colors?.secondary,
                    marginTop: 2,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                }}>
                    {t('options')}: {optionNames.join(', ')}
                </div>
            )}

            <div style={{
                fontSize: '0.75rem',
                color: theme?.colors?.textSecondary,
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
            }}>
                {t('orderName')}: {item.order_name} <br />
                {/* The label the batch was sent under, empty where the tenant
                    does not use order labels */}
                {item.order_label && <>{t('orderLabel')}: {item.order_label} <br /></>}
                {t('user')}: {item.item_user_name}
            </div>

            {item.note && (
                <div style={{
                    fontSize: '0.7rem',
                    color: theme?.colors?.text,
                    fontStyle: 'italic',
                    marginTop: '0.2rem',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                }}>
                    {t('note')}: {item.note}
                </div>
            )}

            {item.dont_make === 1 ? (
                <div style={{
                    backgroundColor: 'rgba(131, 35, 6, 0.2)',
                    color: '#ff5722',
                    padding: '0.25rem 0.5rem',
                    borderRadius: 4,
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    marginTop: '0.25rem'
                }}>
                    {t('dontMake')}
                </div>
            ) : (
                <div style={{
                    fontSize: '0.8rem',
                    color: theme?.colors?.primary,
                    fontStyle: 'italic',
                    marginTop: '0.25rem'
                }}>
                    {status === 0 ? t('clickToStart') : status === 1 ? t('clickToFinish') : t('clickToRemove')}
                </div>
            )}
        </div>
    );
}, (prevProps, nextProps) => {
    // Custom comparison - only re-render if relevant props changed
    return (
        prevProps.item.id === nextProps.item.id &&
        prevProps.item.status === nextProps.item.status &&
        prevProps.item.quantity === nextProps.item.quantity &&
        prevProps.item.note === nextProps.item.note &&
        prevProps.item.dont_make === nextProps.item.dont_make &&
        prevProps.item.product_name === nextProps.item.product_name &&
        prevProps.item.table_name === nextProps.item.table_name &&
        prevProps.item.order_name === nextProps.item.order_name &&
        prevProps.item.order_label === nextProps.item.order_label &&
        prevProps.item.item_user_name === nextProps.item.item_user_name &&
        prevProps.item.options === nextProps.item.options &&
        prevProps.currentTime === nextProps.currentTime &&
        prevProps.theme === nextProps.theme
    );
});

// Memoized OrderColumn component to prevent unnecessary re-renders
const OrderColumn = React.memo(({
    title,
    items,
    status,
    bgColor,
    theme,
    t,
    allOptions,
    updateStatus,
    getTimeSinceOrder,
    getTimeInStatus,
    getTimeToFinish,
    getTimeColor,
    getOptionNames,
    getItemSize,
    listHeight
}) => {
    // Row renderer for virtualized list
    const Row = useCallback(({ index, style }) => {
        const item = items[index];

        return (
            <div style={{
                ...style,
                padding: '0 0.25rem',
                boxSizing: 'border-box',
                overflow: 'hidden'
            }}>
                <OrderItemCard
                    key={item.id}
                    item={item}
                    status={status}
                    theme={theme}
                    t={t}
                    allOptions={allOptions}
                    onStatusUpdate={(itemId, itemStatus) => {
                        if (itemStatus === 0) {
                            updateStatus(itemId, 1);
                        } else if (itemStatus === 1) {
                            updateStatus(itemId, 2);
                        } else if (itemStatus === 2) {
                            updateStatus(itemId, 3);
                        }
                    }}
                    getTimeSinceOrder={getTimeSinceOrder}
                    getTimeInStatus={getTimeInStatus}
                    getTimeToFinish={getTimeToFinish}
                    getTimeColor={getTimeColor}
                    getOptionNames={getOptionNames}
                />
            </div>
        );
    }, [items, status, theme, t, allOptions, updateStatus,
        getTimeSinceOrder, getTimeInStatus, getTimeToFinish, getTimeColor, getOptionNames]);

    return (
        <div style={{
            flex: '1 1 300px',
            margin: '0 0.25rem',
            backgroundColor: theme?.colors?.surface || '#fff',
            borderRadius: theme?.borderRadius || 8,
            boxShadow: theme?.boxShadow,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
        }}>
            <div style={{
                backgroundColor: bgColor,
                color: '#fff',
                padding: '0.5rem',
                borderRadius: `${theme?.borderRadius || 8}px ${theme?.borderRadius || 8}px 0 0`,
                fontWeight: 'bold',
                fontSize: '1rem',
                textAlign: 'center'
            }}>
                {title} ({items.length})
            </div>
            <div style={{ flexGrow: 1, height: listHeight, overflow: 'hidden' }}>
                {items.length > 0 ? (
                    <List
                        height={listHeight}
                        itemCount={items.length}
                        itemSize={(index) => getItemSize(items[index])}
                        width="100%"
                        overscanCount={2}
                    >
                        {Row}
                    </List>
                ) : (
                    <div style={{
                        padding: '1rem',
                        textAlign: 'center',
                        color: theme?.colors?.textSecondary
                    }}>
                        {t('noItems')}
                    </div>
                )}
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    // Custom comparison: return true to SKIP re-render, false to re-render
    return (
        prevProps.items === nextProps.items &&
        prevProps.theme === nextProps.theme
    );
});

function OrderStatus({ theme, defaultFilters = ['all'] }) {
    const [orderItems, setOrderItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedAreas, setSelectedAreas] = useState(defaultFilters);
    const [showPastOrders, setShowPastOrders] = useState(false);
    const [pastOrders, setPastOrders] = useState([]);
    const [allOptions, setAllOptions] = useState([]);
    const [tables, setTables] = useState([]);
    const [tableInfo, setTableInfo] = useState(null);
    const [floorPlanSettings, setFloorPlanSettings] = useState({ floorPlanWidth: 600, floorPlanHeight: 400, gridSnapSize: 0 });
    const [pollInterval, setPollInterval] = useState(3000); // Adaptive polling interval (3 seconds for fast updates)
    const lastItemCountRef = useRef(0); // Track last item count for adaptive polling
    const [lastSyncTimestamp, setLastSyncTimestamp] = useState(null); // For delta sync
    const [useDeltaSync, setUseDeltaSync] = useState(false); // Enable delta sync after first fetch
    const { refresh, refreshKey } = useRefresh();
    const { t } = useLanguage();

    // Sound toggle (persisted per-device in localStorage)
    const [soundEnabled, setSoundEnabled] = useState(() => {
        const stored = localStorage.getItem('kdsSoundEnabled');
        return stored === null ? true : stored === 'true';
    });

    const toggleSound = useCallback(() => {
        setSoundEnabled(prev => {
            const next = !prev;
            localStorage.setItem('kdsSoundEnabled', String(next));
            return next;
        });
    }, []);

    useEffect(() => {
        fetchOrderProducts();
        fetch(`${BACKEND_URL}/api/system-settings`)
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (data) setFloorPlanSettings({
                    floorPlanWidth: parseInt(data.floorPlanWidth) || 600,
                    floorPlanHeight: parseInt(data.floorPlanHeight) || 400,
                    gridSnapSize: parseInt(data.gridSnapSize) || 0,
                });
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAreas, refreshKey]);

    // Adaptive polling - adjusts interval based on activity
    useEffect(() => {
        if (!pollInterval) return; // Paused when window hidden

        const interval = setInterval(() => {
            fetchOrderProducts();
        }, pollInterval);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAreas, pollInterval]);

    // Adjust poll interval based on activity
    useEffect(() => {
        const currentItemCount = orderItems.length;

        // If items changed, poll more frequently for next minute
        if (currentItemCount !== lastItemCountRef.current) {
            setPollInterval(2000); // Poll every 2s when active (very responsive)

            // Reset to normal after 60 seconds of no changes
            const resetTimer = setTimeout(() => {
                setPollInterval(3000); // Back to 3s when idle
            }, 60000);

            lastItemCountRef.current = currentItemCount;
            return () => clearTimeout(resetTimer);
        }
    }, [orderItems.length]);

    // Pause polling when window is hidden (save resources)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Window hidden - stop polling to save resources
                setPollInterval(null);
            } else {
                // Window visible - resume polling and fetch immediately
                setPollInterval(3000);
                fetchOrderProducts();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch all options for mapping ids to names
    useEffect(() => {
        fetch(`${BACKEND_URL}/api/debug/options`)
            .then(res => res.json())
            .then(data => setAllOptions(data))
            .catch(() => setAllOptions([]));
    }, []);

    // Fetch tables for the table display
    useEffect(() => {
        fetch(`${BACKEND_URL}/api/tables`)
            .then(res => res.json())
            .then(data => setTables(data))
            .catch(() => setTables([]));
    }, [refreshKey]);

    // --- Sound Alert System (Web Audio API) ---
    const audioContextRef = useRef(null);
    const seenItemIdsRef = useRef(new Set());
    const unacknowledgedItemsRef = useRef(new Map()); // id -> firstSeenTime (ms)

    const getAudioContext = useCallback(() => {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        if (audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume();
        }
        return audioContextRef.current;
    }, []);

    const playBeep = useCallback((frequency, duration, volume, delay) => {
        try {
            const ctx = getAudioContext();
            const oscillator = ctx.createOscillator();
            const gainNode = ctx.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(ctx.destination);
            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + delay);
            gainNode.gain.setValueAtTime(0, ctx.currentTime + delay);
            gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + delay + 0.01);
            gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + duration);
            oscillator.start(ctx.currentTime + delay);
            oscillator.stop(ctx.currentTime + delay + duration + 0.05);
        } catch (e) {
            console.warn('[OrderStatus] Sound play failed:', e);
        }
    }, [getAudioContext]);

    // New order: pleasant two-tone ascending chime
    const playNewOrderAlert = useCallback(() => {
        playBeep(880, 0.15, 0.4, 0);
        playBeep(1108, 0.2, 0.4, 0.18);
    }, [playBeep]);

    // Escalation: three urgent beeps for long-unacknowledged orders
    const playEscalationAlert = useCallback(() => {
        playBeep(660, 0.12, 0.5, 0);
        playBeep(660, 0.12, 0.5, 0.15);
        playBeep(880, 0.2, 0.5, 0.30);
    }, [playBeep]);

    // Detect new status-0 items and play new-order sound
    useEffect(() => {
        const newItems = orderItems.filter(
            item => item.status === 0 && !seenItemIdsRef.current.has(item.id)
        );

        if (newItems.length > 0) {
            if (soundEnabled) playNewOrderAlert();
            newItems.forEach(item => {
                seenItemIdsRef.current.add(item.id);
                unacknowledgedItemsRef.current.set(item.id, Date.now());
            });
        }

        // Remove from unacknowledged map when item leaves status 0 (acknowledged)
        const currentStatus0Ids = new Set(
            orderItems.filter(item => item.status === 0).map(item => item.id)
        );
        for (const id of unacknowledgedItemsRef.current.keys()) {
            if (!currentStatus0Ids.has(id)) {
                unacknowledgedItemsRef.current.delete(id);
            }
        }

        // Prune seenItemIds for items fully removed from the display
        const allCurrentIds = new Set(orderItems.map(item => item.id));
        for (const id of seenItemIdsRef.current) {
            if (!allCurrentIds.has(id)) {
                seenItemIdsRef.current.delete(id);
            }
        }
    }, [orderItems, playNewOrderAlert, soundEnabled]);

    // Escalation: re-alert every 30s for orders unacknowledged for ≥3 minutes
    useEffect(() => {
        const ESCALATION_CHECK_INTERVAL = 30 * 1000;
        const UNACKNOWLEDGED_THRESHOLD = 3 * 60 * 1000;

        const escalationTimer = setInterval(() => {
            const now = Date.now();
            let shouldEscalate = false;
            for (const firstSeenTime of unacknowledgedItemsRef.current.values()) {
                if (now - firstSeenTime >= UNACKNOWLEDGED_THRESHOLD) {
                    shouldEscalate = true;
                    break;
                }
            }
            if (shouldEscalate && soundEnabled) {
                playEscalationAlert();
            }
        }, ESCALATION_CHECK_INTERVAL);

        return () => clearInterval(escalationTimer);
    }, [playEscalationAlert, soundEnabled]);

    // Merge delta updates into existing order items
    const mergeDeltaUpdates = useCallback((changes) => {
        if (!changes || changes.length === 0) {
            // console.log('[OrderStatus] No changes to merge');
            return;
        }

        // console.log('[OrderStatus] Merging changes:', changes.map(c => `ID:${c.id} Status:${c.status}`).join(', '));

        setOrderItems(prevItems => {
            // Create a map of existing items for quick lookup
            const itemMap = new Map(prevItems.map(item => [item.id, item]));

            // Process each change
            changes.forEach(change => {
                if (change.status === 3 || change.status === 4) {
                    // Item archived or deleted - remove from display
                    // console.log(`[OrderStatus] Removing item ${change.id} (status ${change.status})`);
                    itemMap.delete(change.id);
                } else if (change.status === 0 || change.status === 1 || change.status === 2) {
                    // Item created or updated - add or update
                    // const action = itemMap.has(change.id) ? 'Updated' : 'Added';
                    // console.log(`[OrderStatus] ${action} item ${change.id} (status ${change.status})`);
                    itemMap.set(change.id, change);
                }
            });

            // Convert back to array and sort by created time
            const newItems = Array.from(itemMap.values()).sort((a, b) =>
                new Date(a.item_created) - new Date(b.item_created)
            );

            // console.log(`[OrderStatus] Item count: ${prevItems.length} → ${newItems.length}`);
            return newItems;
        });
    }, []);

    const fetchOrderProducts = useCallback(async (forceFull = false, areasOverride = null) => {
        // Track if we set loading to true, so we only set it false if we changed it
        const shouldShowLoading = forceFull || !useDeltaSync;

        try {
            // Only show loading on initial load or forced refresh, not on polling
            if (shouldShowLoading) {
                setLoading(true);
            }

            // Use delta sync if available and not forced full refresh
            const useDelta = useDeltaSync && lastSyncTimestamp && !forceFull;

            let url = `${BACKEND_URL}/api/order-products${useDelta ? '/delta' : ''}`;
            const params = new URLSearchParams();

            // Add timestamp for delta sync
            if (useDelta) {
                params.append('since', lastSyncTimestamp.toString());
            }

            // Add area filters (use override if provided, otherwise use state)
            const areasToUse = areasOverride || selectedAreas;
            if (!areasToUse.includes('all') && areasToUse.length > 0) {
                areasToUse.forEach(area => {
                    params.append('areas', area);
                });
            }

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await fetch(url);
            if (!response.ok) throw new Error('Failed to fetch order products');

            const data = await response.json();

            if (useDelta) {
                // Merge delta changes into existing state
                mergeDeltaUpdates(data.changes);
                setLastSyncTimestamp(data.timestamp);
            } else {
                // Full refresh
                setOrderItems(data);
                setLastSyncTimestamp(Date.now());
                setUseDeltaSync(true); // Enable delta sync for next poll
            }
        } catch (err) {
            console.error('[OrderStatus] Fetch error:', err);
            setError(err.message);
            // On error, fall back to full refresh next time
            setUseDeltaSync(false);
        } finally {
            // Only set loading false if we set it true
            if (shouldShowLoading) {
                setLoading(false);
            }
        }
    }, [useDeltaSync, lastSyncTimestamp, selectedAreas, mergeDeltaUpdates]);

    const fetchPastOrders = useCallback(async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/order-products/past`);
            if (!response.ok) throw new Error('Failed to fetch past orders');
            const data = await response.json();
            setPastOrders(data);
        } catch (err) {
            alert('Failed to fetch past orders: ' + err.message);
        }
    }, []);

    const updateStatus = useCallback(async (itemId, newStatus) => {
        try {
            // console.log(`[OrderStatus] Updating item ${itemId}: status → ${newStatus}`);
            // const updateStart = Date.now();

            const response = await fetch(`${BACKEND_URL}/api/order-products/${itemId}/status`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus })
            });
            if (!response.ok) throw new Error('Failed to update status');

            // const updateDuration = Date.now() - updateStart;
            // console.log(`[OrderStatus] Status updated in ${updateDuration}ms, fetching updates...`);

            fetchOrderProducts(false); // Use delta sync (not forced full)
            fetchPastOrders(); // Refresh past orders
            refresh(); // Trigger global refresh for OrderTotal
        } catch (err) {
            console.error('[OrderStatus] Update failed:', err);
            alert('Failed to update status: ' + err.message);
        }
    }, [fetchOrderProducts, fetchPastOrders, refresh]);

    const clearAllItems = async () => {
        // Confirm with the user
        if (!window.confirm(t('clearAllItemsConfirm'))) {
            return;
        }

        try {
            const response = await fetch(`${BACKEND_URL}/api/order-products/bulk-status`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: 3 }) // 3 = completed/archived
            });

            if (!response.ok) throw new Error('Failed to clear all items');

            await response.json();
            alert(t('clearAllItemsSuccess'));

            fetchOrderProducts(); // Refresh the data
            fetchPastOrders(); // Refresh past orders
            refresh(); // Trigger global refresh
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const deleteOrderItem = async (itemId) => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/order-products/${itemId}`, {
                method: 'DELETE'
            });
            if (!response.ok) throw new Error('Failed to delete item');
            fetchOrderProducts(); // Refresh the data
            fetchPastOrders(); // Refresh past orders
            refresh(); // Trigger global refresh for OrderTotal
        } catch (err) {
            alert('Failed to delete item: ' + err.message);
        }
    };

    // Returns time since item entered the table (item_created)
    const getTimeSinceOrder = useCallback((item) => {
        const created = new Date(item.item_created);
        const diffMs = Date.now() - created;
        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffMs / (1000 * 60));
        if (diffSecs < 90) return `${diffSecs}s`;
        if (diffMins < 60) return `${diffMins}m`;
        const hours = Math.floor(diffMins / 60);
        const mins = diffMins % 60;
        return `${hours}h ${mins}m`;
    }, []);

    // Tracks time since status change using order_product_times data
    const getTimeInStatus = useCallback((item) => {
        if (!item.status_changed_time) return 'now';
        const changed = new Date(item.status_changed_time);
        const diffMs = Date.now() - changed;
        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffMs / (1000 * 60));
        if (diffSecs < 90) return `${diffSecs}s`;
        if (diffMins < 60) return `${diffMins}m`;
        const hours = Math.floor(diffMins / 60);
        const mins = diffMins % 60;
        return `${hours}h ${mins}m`;
    }, []);

    // Calculate time to finish for finished orders
    const getTimeToFinish = useCallback((item) => {
        if (!item.ordered_timestamp || !item.finished_timestamp) return 'unknown';
        const started = new Date(item.ordered_timestamp);
        const finished = new Date(item.finished_timestamp);
        const diffMs = finished - started;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        if (diffMins < 1) return '<1m';
        if (diffMins < 60) return `${diffMins}m`;
        const hours = Math.floor(diffMins / 60);
        const mins = diffMins % 60;
        return `${hours}h ${mins}m`;
    }, []);


    // Memoized getTimeColor to avoid recalculation on every render
    const getTimeColor = useCallback((item) => {
        const created = new Date(item.item_created);
        const diffMins = Math.floor((Date.now() - created) / (1000 * 60));

        const freshMins = item.fresh_minutes || 5;
        const attentionMins = item.attention_minutes || 15;
        const freshColor = item.fresh_color || '#4caf50';
        const attentionColor = item.attention_color || '#ff9800';
        const urgentColor = item.urgent_color || '#f44336';

        if (diffMins < freshMins) return freshColor;
        if (diffMins < attentionMins) return attentionColor;
        return urgentColor;
    }, []);

    // Memoized groupByStatus with stable array references
    const groupByStatus = useMemo(() => {
        const newGroup0 = [];
        const newGroup1 = [];
        const newGroup2 = [];

        orderItems.forEach(item => {
            if (item.status === 0) {
                newGroup0.push(item);
            } else if (item.status === 1) {
                newGroup1.push(item);
            } else if (item.status === 2) {
                newGroup2.push(item);
            }
        });

        return {
            0: newGroup0,
            1: newGroup1,
            2: newGroup2
        };
    }, [orderItems]);

    const columnListHeight = useMemo(() => {
        const hasAnyItems = groupByStatus[0].length > 0 || groupByStatus[1].length > 0 || groupByStatus[2].length > 0;
        return hasAnyItems ? Math.min(600, window.innerHeight - 200) : 200;
    }, [groupByStatus]);

    const handleAreaToggle = (area) => {
        let newAreas;
        if (area === 'all') {
            newAreas = ['all'];
        } else {
            const withoutAll = selectedAreas.filter(a => a !== 'all');
            if (withoutAll.includes(area)) {
                const filtered = withoutAll.filter(a => a !== area);
                newAreas = filtered.length === 0 ? ['all'] : filtered;
            } else {
                newAreas = [...withoutAll, area];
            }
        }

        // Update state
        setSelectedAreas(newAreas);

        // Force immediate refresh with new filters (pass newAreas directly to avoid race condition)
        setLoading(true);
        fetchOrderProducts(true, newAreas);
    };

    // Memoized helper to get option names from item.options (JSON string or array)
    const getOptionNames = useCallback((item, allOptionsParam) => {
        if (!item.options) return null;
        let optionIds = [];
        if (typeof item.options === 'string') {
            try {
                optionIds = JSON.parse(item.options);
            } catch {
                return null;
            }
        } else if (Array.isArray(item.options)) {
            optionIds = item.options;
        }
        if (!Array.isArray(optionIds) || optionIds.length === 0) return null;
        if (!allOptionsParam) return optionIds;
        // Filter out options with selected === 1
        return optionIds
            .map(id => allOptionsParam.find(o => o.id === id))
            .filter(opt => opt && opt.selected !== 1)
            .map(opt => opt.name);
    }, []);

    // Estimate item height based on content (for virtualization)
    const getItemSize = useCallback((item) => {
        let height = 140; // Base height (reduced from 180)

        // Add height for options
        const optionNames = getOptionNames(item, allOptions);
        if (optionNames && optionNames.length > 0) {
            height += 18;
        }

        // Add height for note
        if (item.note) {
            height += 18;
        }

        // Add height for don't make badge
        if (item.dont_make === 1) {
            height += 8;
        }

        return height;
    }, [allOptions, getOptionNames]);

    if (error) return <div style={{ color: theme?.colors?.error || 'red' }}>{t('error')}: {error}</div>;

    return (
        <div style={{
            padding: '0.5rem',
            backgroundColor: theme?.colors?.background,
            color: theme?.colors?.text,
            minHeight: '100vh'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem',
                flexWrap: 'wrap',
                gap: '0.5rem'
            }}>
                <h2 style={{ color: theme?.colors?.primary, margin: 0, fontSize: '1.25rem' }}>{t('orderStatus')}</h2>
                <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                }}>
                    {['all', 'kitchen', 'bar', 'pizza', 'other'].map(area => (
                        <label
                            key={area}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: '0.25rem 0.5rem',
                                borderRadius: theme?.borderRadius || 6,
                                backgroundColor: selectedAreas.includes(area) ? theme?.colors?.primary : 'transparent',
                                color: selectedAreas.includes(area) ? theme?.colors?.surface : theme?.colors?.text,
                                border: `1px solid ${selectedAreas.includes(area) ? theme?.colors?.primary : (theme?.colors?.border || '#ccc')}`,
                                transition: 'all 0.2s',
                                fontSize: '0.8rem',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={selectedAreas.includes(area)}
                                onChange={() => handleAreaToggle(area)}
                                style={{
                                    marginRight: '0.5rem',
                                    cursor: 'pointer'
                                }}
                            />
                            {area === 'all' ? t('allOrders') : t(area)}
                        </label>
                    ))}

                    {/* Sound Toggle Button */}
                    <button
                        onClick={toggleSound}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: soundEnabled ? (theme?.colors?.success || '#4caf50') : (theme?.colors?.textSecondary || '#999'),
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 6,
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s'
                        }}
                    >
                        {soundEnabled ? '🔔 ' + t('soundOn') : '🔕 ' + t('soundOff')}
                    </button>

                    {/* Manual Refresh Button */}
                    <button
                        onClick={() => {
                            setLoading(true);
                            fetchOrderProducts(true); // Force full refresh
                        }}
                        disabled={loading}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: theme?.colors?.primary || '#1976d2',
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 6,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontWeight: 'bold',
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            opacity: loading ? 0.5 : 1,
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={e => {
                            if (!loading) e.target.style.backgroundColor = theme?.colors?.secondary || '#666';
                        }}
                        onMouseLeave={e => {
                            if (!loading) e.target.style.backgroundColor = theme?.colors?.primary || '#1976d2';
                        }}
                    >
                        {loading ? t('loading') + '...' : '🔄 ' + t('refresh')}
                    </button>

                    {/* Clear All Items Button */}
                    <button
                        onClick={clearAllItems}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: theme?.colors?.warning || '#ff9800',
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 6,
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={e => e.target.style.backgroundColor = theme?.colors?.error || '#f44336'}
                        onMouseLeave={e => e.target.style.backgroundColor = theme?.colors?.warning || '#ff9800'}
                    >
                        {t('clearAllItems')}
                    </button>
                </div>
            </div>

            <div className="order-status-columns" style={{
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'stretch'
            }}>
                <OrderColumn
                    title={t('newOrders')}
                    items={groupByStatus[0]}
                    status={0}
                    bgColor="#2196f3"
                    theme={theme}
                    t={t}
                    allOptions={allOptions}
                    updateStatus={updateStatus}
                    getTimeSinceOrder={getTimeSinceOrder}
                    getTimeInStatus={getTimeInStatus}
                    getTimeToFinish={getTimeToFinish}
                    getTimeColor={getTimeColor}
                    getOptionNames={getOptionNames}
                    getItemSize={getItemSize}
                    listHeight={columnListHeight}
                />
                <OrderColumn
                    title={t('inProgress')}
                    items={groupByStatus[1]}
                    status={1}
                    bgColor="#ff9800"
                    theme={theme}
                    t={t}
                    allOptions={allOptions}
                    updateStatus={updateStatus}
                    getTimeSinceOrder={getTimeSinceOrder}
                    getTimeInStatus={getTimeInStatus}
                    getTimeToFinish={getTimeToFinish}
                    getTimeColor={getTimeColor}
                    getOptionNames={getOptionNames}
                    getItemSize={getItemSize}
                    listHeight={columnListHeight}
                />
                <OrderColumn
                    title={t('finished')}
                    items={groupByStatus[2]}
                    status={2}
                    bgColor="#4caf50"
                    theme={theme}
                    t={t}
                    allOptions={allOptions}
                    updateStatus={updateStatus}
                    getTimeSinceOrder={getTimeSinceOrder}
                    getTimeInStatus={getTimeInStatus}
                    getTimeToFinish={getTimeToFinish}
                    getTimeColor={getTimeColor}
                    getOptionNames={getOptionNames}
                    getItemSize={getItemSize}
                    listHeight={columnListHeight}
                />
            </div>

            {/* Table Display - 1/3 width centered below status columns (mirrors ServerScreen pattern) */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                <div style={{ flex: '0 0 33%', minWidth: 0 }}>
                    <TableDisplay
                        tables={tables}
                        theme={theme}
                        onOrderSelect={() => {}}
                        tableInfo={tableInfo}
                        setTableInfo={setTableInfo}
                        user={null}
                        readOnly={true}
                        floorPlanWidth={floorPlanSettings.floorPlanWidth}
                        floorPlanHeight={floorPlanSettings.floorPlanHeight}
                        gridSnapSize={floorPlanSettings.gridSnapSize}
                    />
                </div>
            </div>

            {/* Past Orders Button and Display */}
            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                <button
                    onClick={() => {
                        if (!showPastOrders) {
                            fetchPastOrders();
                        }
                        setShowPastOrders(!showPastOrders);
                    }}
                    style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: theme?.colors?.secondary || '#666',
                        color: theme?.colors?.surface || '#fff',
                        border: 'none',
                        borderRadius: theme?.borderRadius || 8,
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        boxShadow: theme?.boxShadow,
                    }}
                >
                    {showPastOrders ? t('hidePastOrders') : t('showPastOrders')}
                </button>
            </div>

            {showPastOrders && (
                <div style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem',
                    backgroundColor: theme?.colors?.surface || '#fff',
                    borderRadius: theme?.borderRadius || 8,
                    boxShadow: theme?.boxShadow,
                }}>
                    <h3 style={{ color: theme?.colors?.primary, marginTop: 0 }}>
                        {t('pastOrders')} ({pastOrders.length})
                    </h3>
                    {pastOrders.length > 0 ? (
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            {pastOrders.map(item => (
                                <div
                                    key={item.id}
                                    style={{
                                        marginBottom: '0.5rem',
                                        padding: '0.5rem',
                                        backgroundColor: item.dont_make === 1 ? 'rgba(255, 87, 34, 0.1)' : theme?.colors?.background,
                                        border: item.dont_make === 1 ? '1px solid #ff5722' : '1px solid #ddd',
                                        borderRadius: theme?.borderRadius || 6,
                                    }}
                                >
                                    {/* Prominent Table Display for Past Orders */}
                                    <div style={{
                                        backgroundColor: theme?.colors?.textSecondary || '#666',
                                        color: theme?.colors?.surface || '#fff',
                                        padding: '0.4rem',
                                        borderRadius: theme?.borderRadius || 6,
                                        marginBottom: '0.5rem',
                                        fontWeight: 'bold',
                                        fontSize: '1rem',
                                        textAlign: 'center'
                                    }}>
                                        {t('table')}: {item.table_name}
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}>
                                        <strong style={{ color: theme?.colors?.text }}>
                                            {item.product_name} x{item.quantity}
                                        </strong>
                                        <span style={{
                                            fontSize: '0.8rem',
                                            color: theme?.colors?.textSecondary
                                        }}>
                                            {t('closed')} {getTimeSinceOrder(item)} {t('ago')}<br />
                                            {t('finished')} {t('in')} {getTimeToFinish(item)}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: '0.9rem', color: theme?.colors?.textSecondary }}>
                                        {t('orderName')}: {item.order_name}
                                        <br />
                                        {item.order_label && <>{t('orderLabel')}: {item.order_label}<br /></>}
                                        {t('user')}: {item.item_user_name}
                                    </div>
                                    {item.note && (
                                        <div style={{
                                            fontSize: '0.8rem',
                                            color: theme?.colors?.text,
                                            fontStyle: 'italic'
                                        }}>
                                            {t('note')}: {item.note}
                                        </div>
                                    )}
                                    {item.dont_make === 1 && (
                                        <div style={{
                                            backgroundColor: 'rgba(131, 35, 6, 0.2)',
                                            color: '#ff5722',
                                            padding: '0.25rem 0.5rem',
                                            borderRadius: 4,
                                            fontSize: '0.8rem',
                                            fontWeight: 'bold',
                                            marginTop: '0.25rem'
                                        }}>
                                            {t('dontMake')}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ color: theme?.colors?.textSecondary }}>
                            {t('noPastOrders')}
                        </div>
                    )}
                </div>
            )}

        </div>
    );
}

export default OrderStatus;